import React, { useState, useEffect } from 'react';
import './PollDisplay.css';

/**
 * Poll Display Component
 * Shows a floating, draggable/minimizable poll widget
 * 
 * Props:
 * - poll: current poll object
 * - onVote: callback when user votes
 * - onClose: callback to close poll
 */
const PollDisplay = ({ poll, onVote, onClose }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const handleVote = (optionId) => {
    if (hasVoted) {
      alert('You have already voted on this poll');
      return;
    }
    setHasVoted(true);
    onVote(optionId);
  };

  const handleMouseDown = (e) => {
    if (e.target.closest('button') || e.target.closest('.poll-option')) {
      return; // Don't drag when clicking buttons
    }
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      setPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  if (!poll) return null;

  return (
    <div
      className={`poll-widget ${isMinimized ? 'minimized' : ''}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
    >
      {/* Header - Draggable */}
      <div
        className="poll-header"
        onMouseDown={handleMouseDown}
      >
        <div className="poll-title">
          <span className="poll-icon">🗳️</span>
          <span>Poll</span>
        </div>
        <div className="poll-controls">
          <button
            className="poll-btn-mini"
            onClick={() => setIsMinimized(!isMinimized)}
            title={isMinimized ? 'Expand' : 'Minimize'}
          >
            {isMinimized ? '📈' : '📉'}
          </button>
          <button
            className="poll-btn-mini close"
            onClick={onClose}
            title="Close"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Content */}
      {!isMinimized && (
        <div className="poll-content">
          <div className="poll-question">{poll.question}</div>

          <div className="poll-options">
            {poll.options.map((option) => (
              <div key={option.id} className="poll-option">
                <button
                  className={`option-button ${hasVoted ? 'voted' : ''}`}
                  onClick={() => handleVote(option.id)}
                  disabled={hasVoted || poll.isClosed}
                >
                  <span className="option-text">{option.text}</span>
                </button>

                <div className="option-stats">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${option.percentage || 0}%` }}
                    />
                  </div>
                  <span className="option-count">
                    {option.votes} ({option.percentage || 0}%)
                  </span>
                </div>
              </div>
            ))}
          </div>

          {poll.isClosed && (
            <div className="poll-status closed">Poll Closed</div>
          )}

          <div className="poll-footer">
            <span className="total-votes">Total votes: {poll.totalVotes}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PollDisplay;
